import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { Tabs, Tab, Paper } from "../../components";
import Timeline from "./Timeline";
import TaskList from "./TaskList";
import WorkflowGraph from "../../components/diagram/WorkflowGraph";
import { makeStyles } from "@material-ui/styles";
import { useDevicePixelRatio } from "use-device-pixel-ratio";
import { useTabsList } from "../../contexts/TabsContext";

const useStyles = makeStyles({
  taskWrapper: {
    overflow: "auto",
    padding: 8,
    height: "100%",
  },
  fullHeight: {
    height: "100%",
  },
});

export default function TaskDetails({
  execution,
  dag,
  selectedTask,
  setSelectedTask,
  showOnlyDiagram,
  refresh,
  rightDrawerWidth,
  isFetching,
  deselectTaskAndCloseRightPanel,
}) {
  const [tabIndex, setTabIndex] = useState(0);
  const classes = useStyles();
  const [svgLoading, setSvgLoading] = useState(true);

  const { tabsList } = useTabsList();

  const flowAccess = useMemo(
    () =>
      tabsList?.Execution?.workflows?.flowlevelaccess || {
        Diagram: "True",
        Timeline: "True",
        Tasklist: "True",
      },
    [tabsList]
  );

  const dpr = useDevicePixelRatio({ defaultDpr: 1 });

  // build a list of enabled tabs in order
  const availableTabs = useMemo(() => {
    const tabs = [];
    if (flowAccess.Diagram === "True")
      tabs.push({ key: "Diagram", label: "Diagram" });
    if (flowAccess.Tasklist === "True")
      tabs.push({ key: "Tasklist", label: "Task List" });
    if (flowAccess.Timeline === "True")
      tabs.push({ key: "Timeline", label: "Timeline" });
    return tabs;
  }, [flowAccess]);

  // Memoize tab click handler
  const handleTabClick = useCallback((idx) => {
    setTabIndex(idx);
  }, []);

  // Memoize the Tabs component
  const TabsComponent = useMemo(() => {
    if (showOnlyDiagram) return null;

    return (
      <Tabs value={tabIndex} contextual id="exe-subheader">
        {availableTabs.map((tab, idx) => (
          <Tab
            key={tab.key}
            label={tab.label}
            onClick={() => handleTabClick(idx)}
          />
        ))}
      </Tabs>
    );
  }, [showOnlyDiagram, tabIndex, availableTabs, handleTabClick]);

  // Memoize the WorkflowGraph component
  const WorkflowGraphComponent = useMemo(() => {
    if (
      !(
        flowAccess.Diagram === "True" &&
        (showOnlyDiagram || availableTabs[tabIndex]?.key === "Diagram")
      )
    ) {
      return null;
    }

    return (
      <WorkflowGraph
        selectedTask={selectedTask}
        executionMode={true}
        dag={dag}
        onClick={setSelectedTask}
        showOnlyDiagram={showOnlyDiagram}
        refresh={refresh}
        rightDrawerWidth={rightDrawerWidth}
        componentLoadedFrom={"Execution"}
        svgLoading={svgLoading}
        setSvgLoading={setSvgLoading}
        isFetching={isFetching}
        dpr={dpr}
        deselectTaskAndCloseRightPanel={deselectTaskAndCloseRightPanel}
      />
    );
  }, [
    flowAccess.Diagram,
    showOnlyDiagram,
    availableTabs,
    tabIndex,
    selectedTask,
    dag,
    setSelectedTask,
    refresh,
    rightDrawerWidth,
    svgLoading,
    setSvgLoading,
    isFetching,
    dpr,
    deselectTaskAndCloseRightPanel,
  ]);

  // Memoize the TaskList component
  const TaskListComponent = useMemo(() => {
    if (availableTabs[tabIndex]?.key !== "Tasklist") return null;

    return (
      <TaskList
        workflowId={execution.workflowId}
        selectedTask={selectedTask}
        tasks={execution.tasks}
        dag={dag}
        onClick={setSelectedTask}
      />
    );
  }, [tabIndex, availableTabs, execution, selectedTask, dag, setSelectedTask]);

  // Memoize the Timeline component
  const TimelineComponent = useMemo(() => {
    if (availableTabs[tabIndex]?.key !== "Timeline") return null;

    return (
      <Timeline
        selectedTask={selectedTask}
        tasks={execution.tasks}
        dag={dag}
        onClick={setSelectedTask}
      />
    );
  }, [tabIndex, availableTabs, execution, selectedTask, dag, setSelectedTask]);

  return (
    <div className={classes.taskWrapper}>
      <Paper className={classes.fullHeight}>
        {/* show tabs only for normal view */}
        {TabsComponent}
        {WorkflowGraphComponent}
        {TaskListComponent}
        {TimelineComponent}
      </Paper>
    </div>
  );
}
